import { queryOptions } from '@tanstack/react-query';
import { MRT_ColumnFiltersState } from 'mantine-react-table';

import { contractsApi } from '../apis/contracts';
import { fetchLotsStats } from '../lots-stats';
import { lotsApi } from '../apis/lots';

import { Filters, Globals, useQueriesWithGlobal } from '@data-access';

export const contractsQueries = (global: Globals) => ({
  all: () => [global, 'contracts'],
  lists: () => [...contractsQueries(global).all(), 'list'],
  list: (filters: Filters) =>
    queryOptions({
      queryKey: [...contractsQueries(global).lists(), filters],
      queryFn: () => contractsApi.getAll(filters),
    }),
  details: () => [...contractsQueries(global).all(), 'detail'],
  detail: (id: number, campaignIds?: number[]) =>
    queryOptions({
      queryKey: [...contractsQueries(global).details(), id, campaignIds],
      queryFn: () => contractsApi.getDetails(id, campaignIds),
    }),
  ilotFields: () => [...contractsQueries(global).all(), 'ilot-fields'],
  ilotFieldsDetail: (
    contractId: number,
    params?: { campaignIds?: number[]; speciesIds?: number[] },
  ) =>
    queryOptions({
      queryKey: [...contractsQueries(global).ilotFields(), contractId, params],
      queryFn: () => contractsApi.getIlotFields(contractId, params),
    }),

  batches: () => [...contractsQueries(global).all(), 'batches'],
  batchesLists: () => [...contractsQueries(global).batches(), 'list'],
  batchesList: (filters: { contractId: number }) =>
    queryOptions({
      queryKey: [...contractsQueries(global).batchesLists(), filters],
      queryFn: () => contractsApi.batches.getAll(filters),
    }),
  batchesDetails: () => [...contractsQueries(global).batches(), 'detail'],
  batchesDetail: (id: number) =>
    queryOptions({
      queryKey: [...contractsQueries(global).batchesDetails(), id],
      queryFn: () => contractsApi.batches.getDetails(id),
    }),

  consents: () => [...contractsQueries(global).all(), 'consents'],
  consentInfo: (token: string) =>
    queryOptions({
      queryKey: [...contractsQueries(global).consents(), token],
      queryFn: () => contractsApi.consent.getConsentInfo(token),
    }),

  validations: () => [...contractsQueries(global).all(), 'validations'],
  validationInfo: (token: string) =>
    queryOptions({
      queryKey: [...contractsQueries(global).validations(), token],
      queryFn: () => contractsApi.validation.getValidationInfo(token),
    }),

  validationForms: () => [...contractsQueries(global).all(), 'validationForms'],
  validationFormsLists: () => [
    ...contractsQueries(global).validationForms(),
    'list',
  ],
  validationFormsList: (filters: Filters) =>
    queryOptions({
      queryKey: [...contractsQueries(global).validationFormsLists(), filters],
      queryFn: () => contractsApi.validationForms.getAll(filters),
    }),
  validationFormsDetails: () => [
    ...contractsQueries(global).validationForms(),
    'detail',
  ],
  validationFormsDetail: (id: number) =>
    queryOptions({
      queryKey: [...contractsQueries(global).validationFormsDetails(), id],
      queryFn: () => contractsApi.validationForms.getDetails(id),
    }),

  lots: () => [...contractsQueries(global).all(), 'lots'],
  lotsLists: () => [...contractsQueries(global).lots(), 'list'],
  lotsList: (filters: Filters) =>
    queryOptions({
      queryKey: [...contractsQueries(global).lotsLists(), filters],
      queryFn: () => lotsApi.getAll(filters),
    }),
  lotsStats: (statsBaseFilters: MRT_ColumnFiltersState) =>
    queryOptions({
      queryKey: [...contractsQueries(global).lots(), 'stats', statsBaseFilters],
      queryFn: () => fetchLotsStats(statsBaseFilters),
    }),
});

export const useContractsQueries = () => {
  const queries = useQueriesWithGlobal(contractsQueries);
  return queries;
};
